import React from 'react';
import _ from 'lodash';
import {useDispatch} from 'react-redux';
import Modal from '../../../../../Modal/Modal';
import styled from 'styled-components';
import {labels, currentResource} from '../../../../../../../util/utils';
import RelateItemList from '../../../../../RelateList/RelateItemList';

function ManageBodyRelatedObjectsModal(props) {
	const {
		engagementId,
		documentId,
		bodyId,
		configurationInfo,
		bodyRelatedObjects,
		showModal,
		onClose,
		onRelatedObjectCheckChanged
	} = props;

	const dispatch = useDispatch();

	const {getSelectedEntities} = configurationInfo;

	const [hasChanges, setHasChanges] = React.useState(false);

	// Currently selected items:
	const relatedObjectsData = React.useRef(
		_.cloneDeep(getSelectedEntities ? getSelectedEntities(bodyRelatedObjects) : bodyRelatedObjects)
	);

	function handleOnClose() {
		onClose && onClose();
	}

	const handleOnRelatedObjectCheckChanged = React.useCallback(
		(object, checked) => {
			onRelatedObjectCheckChanged && onRelatedObjectCheckChanged(object, checked);
		},
		[onRelatedObjectCheckChanged]
	);

	const componentHeader = React.useCallback(() => {
		return configurationInfo.componentHeader;
	}, [configurationInfo]);

	const data = React.useCallback(
		(page, pageSize, searchString, parentItem) => {
			if (!configurationInfo.supportsServerSidePagination) {
				return dispatch(configurationInfo.data(engagementId));
			} else {
				return dispatch(configurationInfo.data(engagementId, page, pageSize, searchString, parentItem));
			}
		},
		[engagementId, dispatch, configurationInfo]
	);

	const mapResponse = React.useCallback(
		(response) => {
			const result = configurationInfo.mapResponse(response);
			return result;
		},
		[configurationInfo]
	);

	const render = React.useCallback(
		(item) => {
			return configurationInfo.render(item);
		},
		[configurationInfo]
	);

	// Determines if the item in the list is selected (show as checked)
	const selected = React.useCallback((element) => {
		const existingObject = relatedObjectsData.current.findIndex(
			(object) => object.entityId === element.entityId && object.entityUId === element.entityUId.toString()
		);
		return existingObject !== -1;
	}, []);

	// const childrenSelected = React.useCallback(
	// 	(element) => {
	// 		// if (configurationInfo.children.checkBox === undefined || configurationInfo.children.checkBox === true) {
	// 		if (configurationInfo.children.checkBox !== false) {
	// 			return configurationInfo.children.selected && configurationInfo.children.selected(relatedObjectsData.current, element);
	// 		}
	// 	},
	// 	[configurationInfo]
	// );

	const filter = React.useCallback(
		(element, searchString) => {
			const filterFunction = configurationInfo.filter;
			return filterFunction && filterFunction(element, searchString);
		},
		[configurationInfo]
	);

	const onResolveFunction = React.useCallback(
		(store, state, value) => {
			return configurationInfo.onResolve(store, state, value);
		},
		[configurationInfo]
	);

	const onFilterFunction = React.useCallback(
		(store, state, value) => {
			return configurationInfo.onFilter(store, state, value);
		},
		[configurationInfo]
	);

	const onCheckChange = React.useCallback(
		(element, checked) => {
			setHasChanges(true);
		},
		[configurationInfo]
	);

	const onChildCheckBoxChange = React.useCallback(
		(element, checked) => {
			const onCheckChangeFunction = configurationInfo.children && configurationInfo.children.onCheckBoxChange;

			if (onCheckChangeFunction) {
				setHasChanges(true);

				if (!checked) {
					const objectToRemove = relatedObjectsData.current.find(
						(object) => object.entityId === element.entityId && object.entityUId === element.entityUId.toString()
					);
					if (objectToRemove) {
						element.formBodyRelatedObjectId = objectToRemove.formBodyRelatedObjectId;
					}
				}

				const elementCopy = {...element, entityUId: element.entityUId.toString()};
				let promise = onCheckChangeFunction(engagementId, documentId, bodyId, dispatch, elementCopy, checked);

				if (onRelatedObjectCheckChanged) {
					promise = promise.then(handleOnRelatedObjectCheckChanged(element, checked));
				}

				return promise;
			}
		},
		[
			configurationInfo,
			bodyId,
			dispatch,
			engagementId,
			documentId,
			onRelatedObjectCheckChanged,
			handleOnRelatedObjectCheckChanged
		]
	);

	// Updates selected items list after the API call is made
	const onCheckChanged = React.useCallback((element, value, hierarchy, response) => {
		if (value === true) {
			relatedObjectsData.current.push({
				entityId: element.entityId,
				entityUId: element.entityUId.toString(),
				formBodyRelatedObjectId: response.id
			});
		} else {
			const index = relatedObjectsData.current.findIndex(
				(item) => item.entityId === element.entityId && item.entityUId === element.entityUId.toString()
			);
			if (index !== -1) {
				relatedObjectsData.current.splice(index, 1);
			}
		}
	}, []);

	const children = React.useMemo(() => {
		if (configurationInfo?.children) {
			return {
				checkBox: configurationInfo.children.checkBox,
				data: configurationInfo.children.data,
				filter: configurationInfo.children.filter,
				render: configurationInfo.children.render,
				selected: selected,
				onCheckBoxChange: configurationInfo.children.onCheckBoxChange ? onChildCheckBoxChange : undefined,
				onCheckBoxChanged: onCheckChanged
			};
		}
	}, [configurationInfo, selected, onChildCheckBoxChange, onCheckChanged]);

	const configuration = React.useMemo(() => {
		if (configurationInfo) {
			return {
				noResultsLabel: configurationInfo.noResultsLabel,
				noDataLabel: configurationInfo.noDataLabel,
				checkBox: configurationInfo.checkBox,
				data: data,
				mapResponse: mapResponse,
				render: render,
				children: children,
				selected: configurationInfo.checkBox ? selected : undefined,
				filter: configurationInfo.filter ? filter : undefined,
				onCheckBoxChange: configurationInfo.checkBox ? onCheckChange : undefined,
				onCheckBoxChanged: configurationInfo.checkBox ? onCheckChanged : undefined
			};
		}
	}, [data, mapResponse, render, selected, filter, onCheckChange, onCheckChanged, children, configurationInfo]);

	return (
		<Modal
			show={showModal}
			title={configurationInfo?.modalTitle}
			modalsize={'medium'}
			onHide={handleOnClose}
			showButtons={false}
			hideConfirmButton
			closeBtnTitle={labels.cancelButtonLabel}
			closeTitle={labels.closeLabel}
			modalContainerClass="ManageBodyRelatedObjectsModalContainer"
			modalbodystyle="ManageBodyRelatedObjectsModalBody"
		>
			<StyledManageBodyRelatedObjectsModal className="StyledManageBodyRelatedObjectsModal">
				{configurationInfo && (
					<RelateItemList
						componentHeader={componentHeader}
						search
						searchClearHoverText={labels.clearHoverText}
						paginationIconHover={currentResource.pagingSvgHoverText}
						paginationShowText={labels.pagingShowtext}
						{...configurationInfo.relateListConfiguration}
						onResolve={configurationInfo.onResolve ? onResolveFunction : undefined}
						onFilter={configurationInfo.onFilter ? onFilterFunction : undefined}
						configuration={configuration}
						showSelectedOnlyText={labels.fo_showOnlyRelated}
					/>
				)}
			</StyledManageBodyRelatedObjectsModal>
		</Modal>
	);
}

const StyledManageBodyRelatedObjectsModal = styled.section`
	height: 100%;
	.RelateListContainers {
		.HeaderWrapper {
			align-items: unset;
			.RelatedTasksDocument {
				margin: var(--px-10) 0 0 0;
				h3 {
					font-size: var(--px-16);
					width: 100%;
					margin: 0;
				}
			}
			.relate-list-paging {
				span {
					font-size: var(--px-14);
				}
			}
			.relate-list-toolbar {
				margin: var(--px-15) 0 0 0;
				.RelateItemsListPagination {
					min-height: var(--px-46);
					display: flex;
				}
			}
			.StyledRelateItemsListHeader {
				.RelateItemsListHeaderSelectedOnly {
					max-width: var(--px-200);
					.RelateItemsListHeaderSelectedOnlyCheckbox {
						width: var(--px-175);
						margin-top: var(--px-6);
					}
					.motif-checkbox {
						.motif-checkbox-label {
							width: 85%;
						}
					}
				}
			}
		}
		.relate-list-header--generic-entity {
			display: flex;
			width: 100%;
			.relate-list-header__item-label--generic-entity {
				padding-left: var(--px-30);
				font-weight: bold;
			}
			.relate-list-header__counter--generic-entity {
				margin-left: auto;
				font-weight: bold;
				padding-right: var(--px-5);
			}
		}
		.relate-list-body-wrapper {
			overflow-x: hidden;
			.relate-list-no-results {
				display: flex;
				align-items: center;
				justify-content: center;
				width: 100%;
				height: 100%;
			}
			.relate-item-list-labelTxt {
				width: 100%;
				max-width: 100%;
				line-height: var(--px-20);
				.manage-objects-item--generic-entity {
					width: calc(100% - var(--px-20));
					&.manage-objects-item-WCGW {
						width: initial;
					}
				}
				.custom-icon {
					svg {
						vertical-align: middle;
						margin-left: var(--px-5);
						margin-top: var(--px-3);
						cursor: default;
						fill: var(--neutrals-900);
					}
				}
			}
			.RelateItemListItem {
				.relate-item-list-level1 {
					.relate-list-body-wrapper {
						.relate-item-list-item {
							&.Expand {
								.relate-item-list-labelTxt {
									cursor: default;
								}
							}
						}
					}
				}
			}
		}
		.relate-item-list-item {
			.relate-item-list-loading-indicator {
				margin: 0 0 0 var(--px-50);
				padding: 0;
				width: inherit;
			}
			&.Expand,
			&.Collapse {
				cursor: pointer !important;
			}
		}
	}
`;

export default ManageBodyRelatedObjectsModal;
